import type { Issue, TranscriptionStatus } from "../db/schema";
import { DESCRIPTION_MAX_LENGTH } from "./constants";

export const COMPLETE_ISSUE_STATUSES: ReadonlySet<TranscriptionStatus> = new Set([
  "approved",
  "not_needed"
]);

export const INCOMPLETE_ISSUE_STATUSES: ReadonlySet<TranscriptionStatus> = new Set([
  "needs_note",
  "audio_saved",
  "queued",
  "transcribing",
  "needs_review",
  "error"
]);

const PROCESSING_STATUSES: ReadonlySet<TranscriptionStatus> = new Set([
  "audio_saved",
  "queued",
  "transcribing"
]);

export interface IssueStatusCounts {
  total: number;
  complete: number;
  incomplete: number;
  needsNote: number;
  needsReview: number;
  processing: number;
  spokenReady: number;
  tooLong: number;
  errors: number;
}

export type RoomInspectionTone = "empty" | "ready" | "attention" | "processing" | "error";

export interface RoomInspectionStatus {
  label: string;
  detail: string;
  actionLabel: string;
  issueLabel: string;
  tone: RoomInspectionTone;
  totalCount: number;
  completeCount: number;
  incompleteCount: number;
  progressPercent: number;
}

export interface ExportReadinessReasons {
  needsText: number;
  needsReview: number;
  processing: number;
  spokenReady: number;
  tooLong: number;
  failed: number;
}

export interface ExportReadiness {
  incomplete: Issue[];
  reasons: ExportReadinessReasons;
  blockingReasonCount: number;
  canContinueWithoutNote: boolean;
}

export function isIssueComplete(issue: Issue): boolean {
  return COMPLETE_ISSUE_STATUSES.has(issue.transcriptionStatus) && !isIssueOverPdfLimit(issue);
}

export function getIncompleteIssues(issues: readonly Issue[]): Issue[] {
  return issues.filter(
    (issue) =>
      INCOMPLETE_ISSUE_STATUSES.has(issue.transcriptionStatus) ||
      hasActiveVoiceJob(issue) ||
      hasPendingSpokenNote(issue) ||
      isIssueOverPdfLimit(issue)
  );
}

export function getIssueStatusCounts(issues: readonly Issue[]): IssueStatusCounts {
  let complete = 0;
  let needsNote = 0;
  let needsReview = 0;
  let processing = 0;
  let spokenReady = 0;
  let tooLong = 0;
  let errors = 0;

  for (const issue of issues) {
    if (isIssueOverPdfLimit(issue)) {
      tooLong += 1;
    }
    if (hasPendingSpokenNote(issue)) {
      spokenReady += 1;
    }
    if (hasActiveVoiceJob(issue)) {
      processing += 1;
    }
    if (isIssueComplete(issue)) {
      complete += 1;
      continue;
    }
    if (issue.transcriptionStatus === "error") {
      errors += 1;
      continue;
    }
    if (hasActiveVoiceJob(issue)) {
      continue;
    }
    if (issue.transcriptionStatus === "needs_note") {
      needsNote += 1;
      continue;
    }
    if (issue.transcriptionStatus === "needs_review") {
      needsReview += 1;
      continue;
    }
    if (PROCESSING_STATUSES.has(issue.transcriptionStatus)) {
      processing += 1;
      continue;
    }
  }

  return {
    total: issues.length,
    complete,
    incomplete: issues.length - complete,
    needsNote,
    needsReview,
    processing,
    spokenReady,
    tooLong,
    errors
  };
}

export function getExportReadiness(issues: readonly Issue[]): ExportReadiness {
  const incomplete = getIncompleteIssues(issues);
  const reasons = getExportReadinessReasons(issues);
  const blockingReasonCount =
    reasons.needsReview +
    reasons.processing +
    reasons.spokenReady +
    reasons.tooLong +
    reasons.failed;

  return {
    incomplete,
    reasons,
    blockingReasonCount,
    canContinueWithoutNote: reasons.needsText > 0 && blockingReasonCount === 0
  };
}

export function getExportReadinessReasons(issues: readonly Issue[]): ExportReadinessReasons {
  let needsText = 0;
  let needsReview = 0;
  let processing = 0;
  let spokenReady = 0;
  let tooLong = 0;
  let failed = 0;

  for (const issue of issues) {
    if (isIssueOverPdfLimit(issue)) {
      tooLong += 1;
    }
    if (hasActiveVoiceJob(issue)) {
      processing += 1;
      continue;
    }
    if (hasPendingSpokenNote(issue)) {
      spokenReady += 1;
      continue;
    }
    if (issue.transcriptionStatus === "needs_review") {
      needsReview += 1;
      continue;
    }
    if (issue.transcriptionStatus === "needs_note") {
      needsText += 1;
      continue;
    }
    if (issue.transcriptionStatus === "error") {
      failed += 1;
    }
  }

  return { needsText, needsReview, processing, spokenReady, tooLong, failed };
}

export function getRoomInspectionStatus(issues: readonly Issue[]): RoomInspectionStatus {
  const counts = getIssueStatusCounts(issues);
  const progressPercent = counts.total > 0 ? Math.round((counts.complete / counts.total) * 100) : 0;
  const base = {
    issueLabel: formatIssueCount(counts.total),
    totalCount: counts.total,
    completeCount: counts.complete,
    incompleteCount: counts.incomplete,
    progressPercent
  };

  if (counts.total === 0) {
    return {
      ...base,
      label: "Not started",
      detail: "Add condition note",
      actionLabel: "Start room",
      tone: "empty"
    };
  }

  if (counts.errors > 0) {
    return {
      ...base,
      label: "Voice note needs attention",
      detail: `${formatCount(counts.errors, "transcription error")}`,
      actionLabel: "Resolve notes",
      tone: "error"
    };
  }

  if (counts.processing > 0 && counts.needsNote > 0) {
    return {
      ...base,
      label: "Needs text",
      detail: `${formatCount(counts.needsNote, "missing note")} - ${formatCount(counts.processing, "voice note")} waiting`,
      actionLabel: "Check notes",
      tone: "processing"
    };
  }

  if (counts.processing > 0 && counts.complete > 0) {
    return {
      ...base,
      label: `${counts.complete} ready`,
      detail: `${formatCount(counts.processing, "voice note")} transcribing`,
      actionLabel: "Check progress",
      tone: "processing"
    };
  }

  if (counts.processing > 0) {
    return {
      ...base,
      label: "Voice note waiting",
      detail: `${formatCount(counts.processing, "voice note")} saved locally`,
      actionLabel: "Check progress",
      tone: "processing"
    };
  }

  if (counts.tooLong > 0) {
    return {
      ...base,
      label: "Shorten note",
      detail: `${formatCount(counts.tooLong, "note")} too long for PDF`,
      actionLabel: "Shorten notes",
      tone: "attention"
    };
  }

  if (counts.needsNote > 0) {
    return {
      ...base,
      label: "Needs text",
      detail: `${formatCount(counts.needsNote, "missing note")}`,
      actionLabel: "Add text",
      tone: "attention"
    };
  }

  if (counts.needsReview > 0) {
    return {
      ...base,
      label: counts.spokenReady > 0 ? "Spoken note ready" : "Review note",
      detail: `${formatCount(counts.needsReview, "note")} to review`,
      actionLabel: "Review notes",
      tone: "attention"
    };
  }

  return {
    ...base,
    label: "Ready for PDF",
    detail: `${counts.total} ready`,
    actionLabel: "View room",
    tone: "ready"
  };
}

export function formatIssueCount(count: number): string {
  return formatCount(count, "condition note");
}

export function hasActiveVoiceJob(issue: Issue): boolean {
  return Boolean(issue.activeTranscriptionJobId) || PROCESSING_STATUSES.has(issue.transcriptionStatus);
}

export function hasPendingSpokenNote(issue: Issue): boolean {
  return Boolean(issue.pendingAiDescription || issue.pendingSuggestedDescription || issue.pendingTranscript);
}

export function isIssueOverPdfLimit(issue: Issue): boolean {
  return (issue.description ?? "").length > DESCRIPTION_MAX_LENGTH;
}

function formatCount(count: number, noun: string): string {
  return `${count} ${noun}${count === 1 ? "" : "s"}`;
}
